import type { SignalEventResponse } from "@/api";
import { useTranslation } from "@/i18n";
import { formatDateID } from "@/lib/format";
import { DIMENSION_KEY } from "@/components/risk/risk-dimension-keys";
import { SignalSeverityBadge } from "@/components/shared/signal-badge";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Props {
  signal: SignalEventResponse | null;
  onClose: () => void;
}

/** Detail dialog for a single signal event. Reads directly from
 *  the prop — no detail endpoint exists. */
export function RiskSignalDetailDialog({ signal, onClose }: Props) {
  const { t } = useTranslation();
  const open = signal !== null;

  return (
    <Dialog open={open} onClose={onClose} size="max-w-lg">
      <DialogHeader>
        <DialogTitle>{t("risk.signalDetail.title")}</DialogTitle>
        <DialogDescription>
          {signal
            ? t("risk.signalDetail.subtitle", {
                type: signal.signal_type,
                time: formatDateID(signal.created_at),
              })
            : ""}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {signal && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("risk.signalDetail.severityLabel")}>
                <SignalSeverityBadge severity={signal.severity} />
              </Field>
              <Field label={t("risk.signalDetail.dimensionLabel")}>
                <span className="text-sm text-ocean-900">
                  {DIMENSION_KEY[signal.dimension_affected]
                    ? t(DIMENSION_KEY[signal.dimension_affected])
                    : signal.dimension_affected}
                </span>
              </Field>
              <Field label={t("risk.signalDetail.sourceLabel")}>
                <span className="text-sm text-neutral-700">
                  {signal.signal_source}
                </span>
              </Field>
              <Field label={t("risk.signalDetail.valueLabel")}>
                <span className="text-sm text-ocean-900">
                  {signal.signal_value ?? "—"}
                </span>
              </Field>
              <Field label={t("risk.signalDetail.triggeredLabel")}>
                {signal.triggered_recompute ? (
                  <Badge tone="ocean">
                    {t("risk.signalDetail.triggeredYes")}
                  </Badge>
                ) : (
                  <Badge tone="neutral">
                    {t("risk.signalDetail.triggeredNo")}
                  </Badge>
                )}
              </Field>
              <Field label={t("risk.signalDetail.snapshotLabel")}>
                <span className="break-all text-xs text-neutral-600">
                  {signal.snapshot_id ?? "—"}
                </span>
              </Field>
            </div>

            <div>
              <div className="label-eyebrow">
                {t("risk.signalDetail.metadataLabel")}
              </div>
              <pre className="mt-1 max-h-60 overflow-auto rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-xs leading-snug text-ocean-900">
                {JSON.stringify(signal.event_metadata ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("risk.signalDetail.close")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-3">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
